import React, { useLayoutEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ResponsiveDialog from '../common/ResponsiveDialog';
import e2iApi from '../../services/e2iApi';
import type { Quotation, QuotationLine } from '../../types/e2i';
import { QUOTATION_STATUS } from '../../types/e2i';
import { saveBlob } from '../../utils/formattedSoa';
import {
  adjustOf,
  blobErrorText,
  errorText,
  formatDate,
  formatDateTime,
  mergeQuotationLines,
  quotationFigures,
  quotationLabel,
  round2,
} from '../../utils/e2i';
import type { QuoteFigures } from '../../utils/e2i';
import ActionDialog from './ActionDialog';
import ApprovalStages from './ApprovalStages';
import ClientDecisionDialog, { ClientDecisionInput } from './ClientDecisionDialog';
import QuotationFigures from './QuotationFigures';
import QuotationLineDialog from './QuotationLineDialog';
import QuotationLinesTable from './QuotationLinesTable';
import QuotationPricing from './QuotationPricing';
import SapLine from './SapLine';
import { StatusChip } from './StatusChip';
import { QuoteDraft, draftFigures, draftOfQuotation, draftProblems, markupNumbers, profitNumber, saveInputOf } from './quotationDraft';
import { WorkspaceTabProps, byRevisionDesc, liveJobs } from './workspace';

type Decision = 'approve' | 'reject' | 'revert' | 'send';

const DECISION_TEXT: Record<Decision, { title: string; button: string; help: string; comment: boolean; danger?: boolean }> = {
  approve: { title: 'Approve the quotation', button: 'Approve', help: 'It moves to the next stage; after the last stage it can be sent to the client.', comment: false },
  reject: { title: 'Reject the quotation', button: 'Reject', help: 'This revision ends here and the preparer is told why; a new revision can be prepared.', comment: true, danger: true },
  revert: { title: 'Send the quotation back', button: 'Send back', help: 'It goes back one stage, or to the preparer from the first stage.', comment: true },
  send: {
    title: 'Send to the client',
    button: 'Send to client',
    help: 'PRPO marks the quotation as sent and, when the company posts quotations to SAP, creates the SAP sales quotation for the customer (an earlier sent revision is then closed in SAP). Send the PDF to the client yourself.',
    comment: false,
  },
};

/** The server's figures of a saved quotation (lines, charge and discount worked out for the breakdown). */
const savedFigures = (q: Quotation): QuoteFigures => {
  const charge = adjustOf(q.addChargePct, q.addChargeAmount);
  const discount = adjustOf(q.discountPct, q.discountAmount);
  const f = quotationFigures({ lines: q.lines || [], chargeMode: charge.mode, chargeValue: charge.value, discountMode: discount.mode, discountValue: discount.value, vatRate: q.vatRate });
  return { ...f, subtotal: q.subtotal, vat: q.vatAmount, total: q.total, margin: round2(q.subtotal - f.cost) };
};

/** Quotation revisions: pricing from the estimate, internal approval, sending (SAP), the client's decision, the PDF. */
const QuotationTab: React.FC<WorkspaceTabProps> = ({ ws, config, companyId, currency, onWorkspace, notify, goTo }) => {
  const perms = ws.permissions;
  const quotations = useMemo(() => byRevisionDesc(ws.quotations || []), [ws.quotations]);
  const latest = quotations[0] || null;
  const [selectedId, setSelectedId] = useState<number | null>(latest?.quotationId ?? null);
  const selected = quotations.find((q) => q.quotationId === selectedId) || latest;
  const [draft, setDraft] = useState<QuoteDraft | null>(selected ? draftOfQuotation(selected, config.costTypes) : null);
  const [dirty, setDirtyState] = useState(false);
  const dirtyRef = useRef(false);
  const shownId = useRef<number | null>(selected?.quotationId ?? null);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [lineDialog, setLineDialog] = useState<{ open: boolean; index: number | null }>({ open: false, index: null });
  const [deleteIndex, setDeleteIndex] = useState<number | null>(null);
  const [mergeSel, setMergeSel] = useState<number[]>([]);
  const [decision, setDecision] = useState<Decision | null>(null);
  const [clientOpen, setClientOpen] = useState(false);

  // Counts the edits, so a save that answers after more typing does not drop that typing.
  const editVersion = useRef(0);
  const setDirty = (v: boolean) => { if (v) editVersion.current += 1; dirtyRef.current = v; setDirtyState(v); };

  const latestId = latest?.quotationId ?? null;
  useLayoutEffect(() => { if (latestId !== null) setSelectedId(latestId); }, [latestId]);

  const costTypes = config.costTypes;
  // The server's quotation, unless the user is editing that same quotation (before paint, so a new revision never
  // shows for a frame without its draft).
  useLayoutEffect(() => {
    if (!selected) { setDraft(null); shownId.current = null; dirtyRef.current = false; setDirtyState(false); return; }
    if (shownId.current === selected.quotationId && dirtyRef.current) return;
    shownId.current = selected.quotationId;
    setDraft(draftOfQuotation(selected, costTypes));
    setMergeSel([]);
    dirtyRef.current = false;
    setDirtyState(false);
  }, [selected, costTypes]);

  const isBusy = busy !== null;

  const run = async (key: string, fn: () => Promise<void>) => {
    setBusy(key);
    setError(null);
    try {
      await fn();
    } catch (e: any) {
      setError(errorText(e, 'The action failed'));
    } finally {
      setBusy(null);
    }
  };

  const postedEstimates = byRevisionDesc((ws.estimates || []).filter((e) => e.status === 'Posted'));
  const fromEstimate = (estimateId: number) => run('fromEstimate', async () => {
    const next = await e2iApi.quotationFromEstimate(estimateId);
    onWorkspace(next);
    notify('The quotation was prepared from the estimate', 'success');
  });

  if (!selected || !draft) {
    const est = postedEstimates[0];
    return (
      <Paper sx={{ p: 2 }}>
        <Stack spacing={1.5} alignItems="flex-start">
          <Typography variant="body2" color="text.secondary">
            {est
              ? `No quotation yet. It is prepared from the posted estimate (R${est.revision}): one priced line per estimate line, with the cost types' markups and the profit.`
              : 'No quotation yet. Post the estimate first: the quotation is priced from it.'}
          </Typography>
          {est && perms.canQuote ? (
            <Button startIcon={<AddIcon />} onClick={() => { void fromEstimate(est.estimateId); }} disabled={isBusy}>
              {busy === 'fromEstimate' ? 'Preparing…' : `Prepare the quotation from estimate R${est.revision}`}
            </Button>
          ) : null}
          {!est ? <Button variant="outlined" onClick={() => goTo('estimate')}>Go to the estimate</Button> : null}
          {error ? <Alert severity="error" sx={{ whiteSpace: 'pre-line' }}>{error}</Alert> : null}
        </Stack>
      </Paper>
    );
  }

  const q = selected;
  const label = quotationLabel(q.quotationNo, q.revision);
  const editable = q.status === 'Draft' && perms.canQuote;
  const isLatest = q.quotationId === latest?.quotationId;
  const hasOpenRevision = quotations.some((x) => x.status === 'Draft' || x.status === 'PendingApproval');
  const canRevise = perms.canQuote && isLatest && ['Approved', 'Sent', 'ClientRejected'].includes(q.status) && q.revision < 99 && !hasOpenRevision;
  const jobForIt = liveJobs(ws).some((j) => j.quotationId === q.quotationId);
  const figures = editable ? draftFigures(draft, config, q.vatRate) : savedFigures(q);
  const vatRate = editable ? (config.vatCodes.find((v) => v.code === draft.vatCode)?.rate ?? q.vatRate) : q.vatRate;
  const sapFailed = q.sap?.status === 'Failed';

  const saveDraft = async (): Promise<void> => {
    const version = editVersion.current;
    const next = await e2iApi.saveQuotation(q.quotationId, saveInputOf(draft));
    // What was typed while saving stays on screen, still unsaved.
    if (editVersion.current === version) setDirty(false);
    onWorkspace(next);
  };

  const save = () => run('save', async () => {
    const problems = draftProblems(draft, config, false);
    if (problems.length) throw new Error(`${problems.join('; ')}.`);
    await saveDraft();
    notify('Quotation saved', 'success');
  });

  const submit = () => run('submit', async () => {
    const problems = draftProblems(draft, config, true);
    if (problems.length) throw new Error(`${problems.join('; ')}.`);
    if (dirtyRef.current) await saveDraft();
    const next = await e2iApi.submitQuotation(q.quotationId);
    onWorkspace(next);
    const after = next.quotations.find((x) => x.quotationId === q.quotationId);
    notify(after?.status === 'Approved' ? 'No approval stage applies: the quotation is approved and can be sent' : `${label} was submitted for approval`, 'success');
  });

  const discard = () => {
    setDraft(draftOfQuotation(q, costTypes));
    setMergeSel([]);
    setDirty(false);
  };

  const confirmDecision = async ({ comment }: { comment: string }) => {
    if (!decision) return;
    if (decision === 'send') {
      const next = await e2iApi.sendQuotation(q.quotationId);
      onWorkspace(next);
      setDecision(null);
      const after = next.quotations.find((x) => x.quotationId === q.quotationId);
      if (after?.sap?.status === 'Failed') notify(`SAP refused the quotation${after.sap.error ? `: ${after.sap.error}` : ''}`, 'error');
      else notify(`${label} is marked as sent to the client`, 'success');
      return;
    }
    const next = await e2iApi.decideQuotation(q.quotationId, decision, comment || null);
    onWorkspace(next);
    setDecision(null);
    const after = next.quotations.find((x) => x.quotationId === q.quotationId);
    notify(`${label}: ${QUOTATION_STATUS[(after?.status || q.status) as Quotation['status']]?.label || after?.status || ''}`, 'success');
  };

  const retrySend = () => run('send', async () => {
    const next = await e2iApi.sendQuotation(q.quotationId);
    onWorkspace(next);
    const after = next.quotations.find((x) => x.quotationId === q.quotationId);
    if (after?.sap?.status === 'Failed') throw new Error(`SAP refused the quotation again${after.sap.error ? `: ${after.sap.error}` : ''}`);
    notify(`${label} is in SAP`, 'success');
  });

  const recordClient = async (input: ClientDecisionInput) => {
    const next = await e2iApi.clientDecision(q.quotationId, input);
    onWorkspace(next);
    setClientOpen(false);
    notify(input.decision === 'approved' ? `${label} approved by the client: the job can be created` : `${label} rejected by the client`, 'success');
  };

  const revise = () => run('revise', async () => {
    const next = await e2iApi.reviseQuotation(q.quotationId);
    onWorkspace(next);
    notify('A new revision was opened as a draft from this one', 'success');
  });

  const downloadPdf = () => run('pdf', async () => {
    try {
      const blob = await e2iApi.quotationPdf(q.quotationId);
      saveBlob(blob, `${label.replace(/[\\/]+/g, '-')}.pdf`);
    } catch (e: any) {
      throw new Error(await blobErrorText(e, 'The PDF could not be made'));
    }
  });

  const applyLine = (line: Omit<QuotationLine, 'lineNum'>) => {
    setDraft((d) => {
      if (!d) return d;
      const list = [...d.lines];
      if (lineDialog.index === null) list.push({ ...line, lineNum: list.length + 1 });
      else list[lineDialog.index] = { ...list[lineDialog.index], ...line };
      return { ...d, lines: list };
    });
    setDirty(true);
    setLineDialog((s) => ({ ...s, open: false }));
  };

  const removeLine = (index: number) => {
    setDraft((d) => (d ? { ...d, lines: d.lines.filter((_l, i) => i !== index).map((l, i) => ({ ...l, lineNum: i + 1 })) } : d));
    setMergeSel([]);
    setDirty(true);
    setDeleteIndex(null);
  };

  const merge = () => {
    if (mergeSel.length < 2) return;
    const result = mergeQuotationLines(draft.lines, mergeSel);
    setDraft({ ...draft, lines: result.lines });
    setMergeSel([]);
    setDirty(true);
    // The merged line's description is the joined descriptions: let the user word it for the client.
    setLineDialog({ open: true, index: result.index });
  };

  const toggle = (index: number) => setMergeSel((s) => (s.includes(index) ? s.filter((i) => i !== index) : [...s, index]));
  const changeDraft = (next: QuoteDraft) => { setDraft(next); setDirty(true); };
  const showApproval = (q.approval?.stages || []).length > 0 || q.status === 'PendingApproval';

  return (
    <Stack spacing={2}>
      <Paper sx={{ p: 2 }}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} flexWrap="wrap" useFlexGap>
          <TextField
            id="e2i-q-revision"
            select
            label="Revision"
            value={q.quotationId}
            onChange={(e) => setSelectedId(Number(e.target.value))}
            disabled={dirty}
            helperText={dirty ? 'Save or discard the changes to see another revision' : undefined}
            sx={{ minWidth: 240 }}
          >
            {quotations.map((x) => <MenuItem key={x.quotationId} value={x.quotationId}>R{x.revision} · {QUOTATION_STATUS[x.status]?.label || x.status}</MenuItem>)}
          </TextField>
          <Box>
            <Typography variant="body2" fontWeight={600}>{label}</Typography>
            <Typography variant="caption" color="text.secondary">
              {[q.preparedByName ? `Prepared by ${q.preparedByName}` : '', q.sentAt ? `sent ${formatDateTime(q.sentAt)}` : '', q.validUntil ? `valid until ${formatDate(q.validUntil)}` : '']
                .filter(Boolean).join(' · ')}
            </Typography>
          </Box>
          <StatusChip status={QUOTATION_STATUS[q.status]} fallback={q.status} />
          <Box sx={{ flexGrow: 1 }} />
          {dirty ? <Typography variant="caption" color="text.secondary">Unsaved changes</Typography> : null}
          {editable ? (
            <>
              {dirty ? <Button size="small" variant="text" onClick={discard} disabled={isBusy}>Discard changes</Button> : null}
              <Button size="small" variant="outlined" onClick={() => { void save(); }} disabled={isBusy || !dirty}>{busy === 'save' ? 'Saving…' : 'Save'}</Button>
              <Button size="small" onClick={() => { void submit(); }} disabled={isBusy}>{busy === 'submit' ? 'Submitting…' : 'Submit for approval'}</Button>
            </>
          ) : null}
          {q.status === 'PendingApproval' && perms.canDecideQuotation ? (
            <>
              <Button size="small" variant="outlined" onClick={() => setDecision('revert')} disabled={isBusy}>Send back</Button>
              <Button size="small" variant="outlined" color="error" onClick={() => setDecision('reject')} disabled={isBusy}>Reject</Button>
              <Button size="small" onClick={() => setDecision('approve')} disabled={isBusy}>Approve</Button>
            </>
          ) : null}
          {q.status === 'Approved' && perms.canSendQuotation && !sapFailed && q.sap?.status !== 'Posting' ? (
            <Button size="small" onClick={() => setDecision('send')} disabled={isBusy}>Send to client</Button>
          ) : null}
          {q.status === 'Sent' && perms.canRecordClientDecision ? (
            <Button size="small" onClick={() => setClientOpen(true)} disabled={isBusy}>Record client decision</Button>
          ) : null}
          {canRevise ? <Button size="small" variant="outlined" onClick={() => { void revise(); }} disabled={isBusy}>{busy === 'revise' ? 'Opening…' : 'Revise'}</Button> : null}
          {q.status === 'ClientApproved' && perms.canCreateJob && !jobForIt ? (
            <Button size="small" onClick={() => goTo('job')} disabled={isBusy}>Create the job</Button>
          ) : null}
          <Button size="small" variant="outlined" startIcon={<PictureAsPdfIcon />} onClick={() => { void downloadPdf(); }} disabled={isBusy || dirty}>
            {busy === 'pdf' ? 'Making the PDF…' : 'PDF'}
          </Button>
        </Stack>
        {error ? <Alert severity="error" onClose={() => setError(null)} sx={{ mt: 1.5, whiteSpace: 'pre-line' }}>{error}</Alert> : null}
      </Paper>

      {q.sap ? (
        <SapLine
          status={q.sap.status}
          error={q.sap.error}
          action={sapFailed && perms.canSendQuotation ? (
            <Button color="inherit" size="small" onClick={() => { void retrySend(); }} disabled={isBusy}>{busy === 'send' ? 'Sending…' : 'Try again'}</Button>
          ) : undefined}
        >
          {q.sap.status === 'Posting'
            ? 'Creating the SAP sales quotation…'
            : q.sap.status === 'Posted'
              ? `SAP sales quotation ${q.sap.docNum || ''}${q.sap.docEntry ? ` (entry ${q.sap.docEntry})` : ''}`
              : 'SAP refused the sales quotation; the quotation stays approved until it is sent'}
        </SapLine>
      ) : null}

      {q.client ? (
        <Alert severity={q.client.decision === 'approved' ? 'success' : 'warning'}>
          {q.client.decision === 'approved' ? 'Approved by the client' : 'Rejected by the client'} on {formatDate(q.client.at)}
          {q.client.poRef ? ` · PO ${q.client.poRef}` : ''}
          {q.client.poDate ? ` of ${formatDate(q.client.poDate)}` : ''}
          {q.client.note ? ` · ${q.client.note}` : ''}
        </Alert>
      ) : null}

      {showApproval ? (
        <Paper sx={{ p: 2 }}>
          <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 600, mb: 1 }}>Approval</Typography>
          <ApprovalStages approval={q.approval} emptyText="No approval stage applies to this quotation." />
        </Paper>
      ) : null}

      <Paper sx={{ p: 2 }}>
        <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 600, mb: 1.5 }}>Pricing and terms</Typography>
        <QuotationPricing idPrefix="e2i-q" config={config} currency={currency} quotation={q} draft={draft} onChange={changeDraft} editable={editable} />
      </Paper>

      <Paper sx={{ p: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1.5 }}>
          <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 600, mr: 'auto' }}>Priced lines</Typography>
          {editable ? (
            <>
              <Button size="small" variant="outlined" onClick={merge} disabled={isBusy || mergeSel.length < 2}>
                {mergeSel.length >= 2 ? `Merge ${mergeSel.length} lines` : 'Merge selected lines'}
              </Button>
              <Button size="small" variant="outlined" startIcon={<AddIcon />} onClick={() => setLineDialog({ open: true, index: null })} disabled={isBusy}>Add priced line</Button>
            </>
          ) : null}
        </Stack>
        {editable ? (
          <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0, mb: 1 }}>
            The descriptions and prices below are what the client sees and what SAP gets. Merged and fixed-price lines keep their price when the markups change.
          </Typography>
        ) : null}
        <QuotationLinesTable
          lines={editable ? draft.lines : q.lines}
          costTypes={costTypes}
          currency={currency}
          docMode={config.docMode}
          editable={editable}
          disabled={isBusy}
          selected={mergeSel}
          onToggle={toggle}
          onEdit={(i) => setLineDialog({ open: true, index: i })}
          onDelete={(i) => setDeleteIndex(i)}
        />
        <Box sx={{ mt: 2 }}>
          <QuotationFigures
            figures={figures}
            currency={currency}
            vatCode={editable ? draft.vatCode || null : q.vatCode}
            vatRate={vatRate}
            chargeReason={editable ? draft.chargeReason : q.addChargeReason}
            preview={editable && dirty}
          />
        </Box>
      </Paper>

      <QuotationLineDialog
        open={lineDialog.open}
        line={lineDialog.index !== null ? draft.lines[lineDialog.index] || null : null}
        companyId={companyId}
        costTypes={costTypes}
        markups={markupNumbers(draft)}
        profitPct={profitNumber(draft)}
        docMode={config.docMode}
        currency={currency}
        onClose={() => setLineDialog((s) => ({ ...s, open: false }))}
        onSave={applyLine}
      />

      <ResponsiveDialog open={deleteIndex !== null} onClose={() => setDeleteIndex(null)} maxWidth="xs" fullWidth mobileFullScreen={false} aria-labelledby="e2i-q-del-title">
        <DialogTitle id="e2i-q-del-title">Remove line {deleteIndex !== null ? deleteIndex + 1 : ''}?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">{deleteIndex !== null ? draft.lines[deleteIndex]?.description : ''}</Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={() => setDeleteIndex(null)}>Keep</Button>
          <Button color="error" onClick={() => { if (deleteIndex !== null) removeLine(deleteIndex); }}>Remove</Button>
        </DialogActions>
      </ResponsiveDialog>

      <ActionDialog
        open={!!decision}
        id="e2i-q-decision"
        title={decision ? `${DECISION_TEXT[decision].title}: ${label}` : ''}
        help={decision ? DECISION_TEXT[decision].help : undefined}
        confirmLabel={decision ? DECISION_TEXT[decision].button : 'OK'}
        danger={decision ? DECISION_TEXT[decision].danger : false}
        commentLabel={decision && decision !== 'send' ? 'Comment' : undefined}
        commentRequired={decision ? DECISION_TEXT[decision].comment : false}
        onClose={() => setDecision(null)}
        onConfirm={confirmDecision}
      />

      <ClientDecisionDialog open={clientOpen} quotationText={label} onClose={() => setClientOpen(false)} onSave={recordClient} />
    </Stack>
  );
};

export default QuotationTab;
